import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import {
  ProductAddOnInterface,
  ProductInterface,
  QuotationInterface,
  MotorRequestInterface,
  VehicleListingInterface,
  PromoCodeInterface,
  ProductDocumentInterface,
  TRPaymentPayloadInterface,
  MotorMembershipInterface
} from "../interface/motor.interface";

@Injectable({
  providedIn: "root",
})
export class MotorDataDataService {
  private basePath: string = `${environment.url}/api/Motor/`;
  private crmBasePath: string = `${environment.crmUrl}/api/Motor/MotorQuotation/`;

  constructor(private http: HttpClient) {}

  getDetailById(id: string) {
    return this.http.get<MotorRequestInterface>(
      `${this.basePath}GetRequestInfoById/${id}`,
    );
  }

  getQuotationById(id: string, getProductDocument?: boolean) {
    let path = `${this.basePath}GetQuotation/${id}`;
    if (getProductDocument) {
      path += "?getProductDocument=true";
    }

    return this.http.get<QuotationInterface[]>(path);
  }

  getAccessList(id: string) {
    return this.http.get<any>(`${this.basePath}GetMotorExcess/${id}`);
  }

  getVehicleListing(limit: number, offset: number, text: string, make: string) {
    return this.http.post<VehicleListingInterface[]>(
      `${this.basePath}GetVehicleModelListing`,
      {
        limit,
        offset,
        text,
        make,
      },
    );
  }

  getAllProduct(id: string) {
    return this.http.get(`${this.basePath}GetAllProduct/${id}`);
  }

  getNonQuoteProduct(id: string) {
    return this.http.get<ProductInterface[]>(
      `${this.basePath}GetNonQuoteProduct/${id}`,
    );
  }

  getAddOn(id: string, quotationId: string) {
    return this.http.get<ProductAddOnInterface[]>(
      `${this.basePath}GetAddOn/${id}?quotationId=${quotationId}`,
    );
  }

  getMotorMembership(id: string, quotationId: string) {
    return this.http.get<MotorMembershipInterface>(
      `${this.basePath}GetMotorMembership/${id}?quotationId=${quotationId}`,
    );
  }

  applyPromoCode(
    id: string,
    code: string,
    vehicleNo: string,
    identityNo: string,
    totalGrossPremium: number,
    motorQuotationId?: string,
  ) {
    return this.http.post<PromoCodeInterface>(
      `${this.basePath}PromoCode/${id}`,
      {
        code,
        totalGrossPremium,
        motorQuotationId,
        vehicleNo, 
        identityNo,
      }, 
    );
  }

  editRequest(id: string, val: any) {
    return this.http.post<void>(`${this.basePath}EditRequest/${id}`, val);
  }

  getProductDocumentById(id: string, language: string, insuranceType: string) {
    return this.http.get<ProductDocumentInterface[]>(
      `${this.basePath}GetProductDocument?id=${id}&language=${language}&insuranceType=${insuranceType}`,
    );
  }

  getMotorcycleProductDocumentById(id: string, language: string, insuranceType: string) {
    return this.http.get<ProductDocumentInterface[]>(
      `${this.basePath}GetMotorcycleProductDocument?id=${id}&language=${language}&insuranceType=${insuranceType}`,
    );
  }

  generatePayment(id: string, val: any) {
    return this.http.post<{redirectUrl: any; id: string }>(
      `${this.basePath}GeneratePayment/${id}`,
      val,
    );
  }

  generateTopupPayment(id: string, value: TRPaymentPayloadInterface) {
    return this.http.post<{ id: string }>(
      `${this.basePath}GenerateTopUpPayment/${id}`,
      value,
    );
  }

  getPaymentById(id: string) {
    return this.http.get<{
      provider: "IPay88" | "Shopback" | "Razer" | "Payex";
      data: string;
    }>(`${this.basePath}GetPayment/${id}`);
  }

  checkPayment(refNo: string) {
    return this.http.get<{ status: boolean; data: any; amount: number; isGeneraliIQ: boolean }>(
      `${this.basePath}CheckPayment?refNo=${refNo}`,
    );
  }

  uploadPhoto(id: string, file: any) {
    return this.http.post<{ id: string }>(
      `${this.basePath}UploadPolicyPhotos/${id}`,
      file,
    );
  }

  deletePolicyPhoto(id: string) {
    return this.http.get(`${this.basePath}DeletePolicyPhoto/${id}`);
  }

  getPolicyPhotos(id: string, includeBase64InsteadOfUrl?: boolean) {
    let path = `${this.basePath}GetPolicyPhotos/${id}`;
    if (includeBase64InsteadOfUrl) {
      path += "?includeBase64InsteadOfUrl=true";
    }

    return this.http.get(path);
  }

  getPolicyTopUpDetail(id: string) {
    return this.http.get(`${this.basePath}GetPolicyTopUpDetail/${id}`);
  }

  editCarVariant(linkId: string, vehicleId: string) {
    return this.http.post<any>(
      `${this.basePath}EditCarVariant/${linkId}?vehicleId=${vehicleId}`,
      null,
    );
  }

  getEPPRates(promoCodeId: string = null) {
    let path = `${this.basePath}GetEppRates`;

    if(promoCodeId)
    {
      path += `?promoCodeId=${promoCodeId}`
    }
    
    return this.http.get(path);
  }

  getAvailableProduct(id: string) {
    return this.http.get(`${this.basePath}GetAvailableProductByLinkId/${id}`);
  }

  checkIsOfficeHour() {
    return this.http.get(`${this.basePath}CheckIsOfficeHour`, {
      responseType: "text",
    });
  }

  getAvailableSalesCampaign(id?: string) {
    return this.http.get(
      `${this.basePath}GetAvailableSalesCampaign?id=${id ? id : ""}`,
    );
  }

  getAvailableBanners() {
    return this.http.get(`${this.basePath}GetActiveBanners`);
  }

  getBNPLRateListByPortal(portal: string) {
    return this.http.get(`${this.basePath}GetBNPLRateListByPortal?portal=${portal}`);
  }

  getGeneraliQuotationPricing(id: string, selectedAddOns: any[], addDriversInfo: any[], windshieldSI: number, gcktSI: number, cartDays: number, cartDailyCoverage: number){
    return this.http.post(`${this.crmBasePath}GetGeneraliQuotationPricing/${id}`, {selectedAddOns, addDriversInfo, windshieldSI, gcktSI, cartDays, cartDailyCoverage})
  }

  getChubbQuotationPricing(id: string, selectedAddOns: any[], windshieldSI: number){
    return this.http.post(`${this.crmBasePath}GetChubbQuotationPricing/${id}`, {selectedAddOns, windshieldSI})
  }

  getTMIMQuotationPricing(selectedAddon: any[], windscreenValue: number | null, quotationId: string, driverCount: number | null) {
    const requestData = {
      QuotationId: quotationId,
      SelectedAddon: selectedAddon,
      windscreenValue: windscreenValue,
      DriverCount : driverCount
    };
    return this.http.post(`${this.crmBasePath}GetTMIMFinalPrem`, requestData);
  }

  getCARTAddOnPriceList() {
    return this.http.get(`${this.basePath}GetCARTAddOnPriceList`);
  }

  secureDiscountDeal(vehicleNo: string, identityNo: string) {
    return this.http.post<any>(`${this.basePath}SecureDiscountDeal`,
      {
        vehicleNo, 
        identityNo,
      }, 
    );
  }
}
